import React from "react";
import { useTranslation } from "react-i18next";
import { invalidate } from "@react-three/fiber";
import { Tooltip } from "flowbite-react";
import { observer } from "mobx-react-lite";
import { collectUsedNcsColorsFromPlan } from "../../../../features/ncsColor";
import { DEFAULT_CEILING_COLOR } from "../../../../features/floorplan3d/structures/ceilings/Ceiling3DLayer";
import { editorStore } from "../../../../store/editorStore";
import { floorplannerStore } from "../../../../store/floorplannerStore";
import { BONI_CHECKBOX_CLASSNAME } from "../../../../constants/brandColors";
import {
  hexColorToNumber,
  numberToHexColor,
} from "../../../../utils/fillSelection";
import { formatLightingTimeOfDay } from "../propertyPanelUtils";
import FillColorControl from "../components/FillColorControl";
import PanelSection from "../components/PanelSection";

type LightingSectionProps = {
  open: boolean;
  onToggle: () => void;
};

const LightingSection: React.FC<LightingSectionProps> = observer(
  ({ open, onToggle }) => {
    const { t } = useTranslation();
    const lightingTimeLabel = formatLightingTimeOfDay(
      floorplannerStore.lighting.sunTimeOfDay,
    );
    const ceilingColorHex = numberToHexColor(
      floorplannerStore.lighting.ceilingColor,
    );
    const pinnedNcsColors = collectUsedNcsColorsFromPlan();

    return (
      <PanelSection
        title={t("propertyPanel.lighting3D")}
        collapsible
        open={open}
        onToggle={onToggle}
        maxHeightClassName="max-h-[720px]"
      >
        <div className="text-sm font-normal p-3">
          <label className="flex items-center justify-between py-2">
            <span>{t("propertyPanel.sunlight")}</span>
            <input
              className={BONI_CHECKBOX_CLASSNAME}
              type="checkbox"
              checked={floorplannerStore.lighting.sunEnabled}
              onChange={(e) => {
                floorplannerStore.updateLighting({
                  sunEnabled: e.target.checked,
                });
                invalidate();
              }}
            />
          </label>
          <div className="py-3">
            <div className="mb-2 flex items-center justify-between">
              <span>{t("propertyPanel.timeOfDay")}</span>
              <span className="text-xs text-gray-500">{lightingTimeLabel}</span>
            </div>
            <input
              className="w-full accent-boniYellowPrimary"
              type="range"
              min={0}
              max={100}
              step={1}
              value={Math.round(floorplannerStore.lighting.sunTimeOfDay * 100)}
              disabled={!floorplannerStore.lighting.sunEnabled}
              onChange={(e) => {
                floorplannerStore.updateLighting({
                  sunTimeOfDay: Number(e.target.value) / 100,
                });
                invalidate();
              }}
            />
            <div className="mt-1 flex justify-between text-[11px] text-gray-500">
              <span>{t("propertyPanel.morning")}</span>
              <span>{t("propertyPanel.noon")}</span>
              <span>{t("propertyPanel.night")}</span>
            </div>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="inline-flex items-center gap-1.5">
              <label htmlFor="room-ceiling-lights" className="cursor-pointer">
                {t("propertyPanel.roomCeilingLights")}
              </label>
              <Tooltip
                content={t("propertyPanel.roomCeilingLightsHint")}
                placement="top"
                style="dark"
              >
                <button
                  type="button"
                  className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-gray-400 text-[10px] font-medium leading-none text-gray-500 hover:border-gray-600 hover:text-gray-700"
                  aria-label={t("propertyPanel.roomCeilingLightsHint")}
                >
                  ?
                </button>
              </Tooltip>
            </span>
            <input
              id="room-ceiling-lights"
              className={BONI_CHECKBOX_CLASSNAME}
              type="checkbox"
              checked={floorplannerStore.lighting.roomLightsEnabled}
              onChange={(e) => {
                floorplannerStore.updateLighting({
                  roomLightsEnabled: e.target.checked,
                });
                invalidate();
              }}
            />
          </div>
          <label className="flex items-center justify-between py-2">
            <span>{t("propertyPanel.showCeilings")}</span>
            <input
              className={BONI_CHECKBOX_CLASSNAME}
              type="checkbox"
              checked={editorStore.showCeilings}
              onChange={(e) => {
                editorStore.setShowCeilings(e.target.checked);
                invalidate();
              }}
            />
          </label>
          {/*
            Compact ceiling color + NCS (palette opens on click). Placed after
            lighting toggles so sunlight / time-of-day stay reachable first.
          */}
          <FillColorControl
            label={t("propertyPanel.ceilingColor")}
            value={ceilingColorHex}
            pinnedColors={pinnedNcsColors}
            onChange={(value) => {
              floorplannerStore.updateLighting({
                ceilingColor: hexColorToNumber(value),
              });
              invalidate();
            }}
            onClear={() => {
              floorplannerStore.updateLighting({
                ceilingColor: DEFAULT_CEILING_COLOR,
              });
              invalidate();
            }}
          />
        </div>
      </PanelSection>
    );
  },
);

export default LightingSection;
